import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { awardeesList } from '../components/awards/AwardeeData';

const AwardeeDetailPage = () => {
  const { id } = useParams();
  const awardeeIndex = awardeesList.findIndex((a) => a.id === id);
  const awardee = awardeesList[awardeeIndex];

  if (!awardee) {
    return (
      <section className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-14 text-center md:px-12">
        <div className="text-6xl">🏆</div>
        <h1 className="mt-4 text-3xl font-bold text-accent">Awardee Not Found</h1>
        <p className="mt-2 text-gray-600">
          The awardee you are looking for does not exist.
        </p>
        <Link
          to="/"
          className="mt-8 rounded-full bg-accent px-8 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-accent/90"
        >
          Back to Home
        </Link>
      </section>
    );
  }

  return (
    <div className="bg-bg-light">
      {/* Back bar */}
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3 md:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-accent"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            All Awardees
          </Link>

          {awardeeIndex > 0 && awardeeIndex < awardeesList.length - 1 && (
            <div className="flex items-center gap-3 text-sm">
              <Link
                to={`/awards/${awardeesList[awardeeIndex - 1].id}`}
                className="inline-flex items-center gap-1 font-medium text-gray-600 transition-colors hover:text-accent"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Prev
              </Link>
              <Link
                to={`/awards/${awardeesList[awardeeIndex + 1].id}`}
                className="inline-flex items-center gap-1 font-medium text-gray-600 transition-colors hover:text-accent"
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-accent to-accent/70">
        <div className="pointer-events-none absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 40%, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative mx-auto max-w-5xl px-6 py-12 md:px-8 md:py-16">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 12a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1zM11 2a1 1 0 011 1v5a1 1 0 01-1 1H6a1 1 0 010-2h4V3a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Drishtikon Himmatwaali Mission
          </p>
          <h1 className="text-4xl font-bold uppercase leading-tight tracking-tight text-white md:text-6xl">
            {awardee.name}
          </h1>
          <div className="mt-4 flex items-center gap-3">
            <span className="h-1 w-16 rounded-full bg-white/80" />
            <span className="text-sm font-medium uppercase tracking-widest text-white/80">
              A Story of Courage
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-5xl px-6 py-12 md:px-8 md:py-16">
        <div className="grid gap-10 md:grid-cols-12 md:gap-12">
          {/* Image */}
          <div className="md:col-span-4">
            <div className="md:sticky md:top-24">
              <div className="overflow-hidden rounded-2xl shadow-xl ring-1 ring-black/5">
                <img
                  src={awardee.image}
                  alt={awardee.imageAlt}
                  className="h-auto w-full object-cover"
                />
              </div>
              <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-accent">
                  About {awardee.name.replace('Awardee ', '')}
                </h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  Honored with the DRISHTIKON HIMMATWAALI MISSION AWARD for her
                  extraordinary courage and resilience in the face of adversity.
                </p>
              </div>
            </div>
          </div>

          {/* Story */}
          <div className="md:col-span-8">
            <div className="rounded-3xl bg-white p-6 shadow-md ring-1 ring-black/5 md:p-10">
              <h2 className="mb-6 text-2xl font-bold text-accent">
                Her Journey
              </h2>
              <div className="text-base leading-relaxed text-gray-700 md:text-lg md:leading-8">
                {awardee.paragraphs.map((paragraph, i) => (
                  <p
                    key={i}
                    className={i === 0 ? "first-letter:float-left first-letter:mr-3 first-letter:text-6xl first-letter:font-bold first-letter:leading-none first-letter:text-accent" : "mt-6"}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom nav */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-5 sm:flex-row">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-accent/90"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            View All Awardees
          </Link>
          {awardeeIndex < awardeesList.length - 1 && (
            <Link
              to={`/awards/${awardeesList[awardeeIndex + 1].id}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition-colors hover:text-accent/80"
            >
              Next Awardee: {awardeesList[awardeeIndex + 1].name}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default AwardeeDetailPage;
